import React, { useEffect, useState } from 'react'
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import { useDispatch, useSelector } from 'react-redux'
import LanguageTranslator from '../../../../locals/LanguageTranslator'
import { dispatchHandler } from '../../../../helper/apiHelperFunctions'
import { formatNumber } from '../../../../helper/genericFunction'

// "Sales Overview" : the sell through donut on the left and the 12 month sales trend on the right.
const SalesTrendsCard = () => {
    const dispatch = useDispatch();
    const getSalesOverviewSummaryData = useSelector(state => state?.digivendStore?.getSalesOverviewSummaryData || {});
    const getGrcReceivedSummaryData = useSelector(state => state?.digivendStore?.getGrcReceivedSummaryData || {});
    const getSalesTrendSummaryData = useSelector(state => state?.digivendStore?.getSalesTrendSummaryData || {});

    const [sellThroughPct, setSellThroughPct] = useState(0);
    const [trendData, setTrendData] = useState([]);

    useEffect(() => {
        dispatchHandler(dispatch, "getSalesTrendSummaryRequest", { type: "SALES_TREND", filter: {} });
    }, [dispatch]);

    useEffect(() => {
        const salesResource = getSalesOverviewSummaryData?.data?.data?.resource || getSalesOverviewSummaryData?.data?.resource;
        const grcResource = getGrcReceivedSummaryData?.data?.data?.resource || getGrcReceivedSummaryData?.data?.resource;

        if (Array.isArray(salesResource) && grcResource) {
            const totalSalesQty = salesResource.reduce((sum, item) => sum + (Number(item?.sales_qty) || 0), 0);
            const totalGrcQty = Number(grcResource?.total_grc_qty) || 0;
            setSellThroughPct(totalGrcQty ? Number(((totalSalesQty / totalGrcQty) * 100).toFixed(1)) : 0);
        }
    }, [getSalesOverviewSummaryData?.data, getGrcReceivedSummaryData?.data]);

    useEffect(() => {
        if (getSalesTrendSummaryData?.isSuccess) {
            const resource = getSalesTrendSummaryData?.data?.data?.resource || getSalesTrendSummaryData?.data?.resource || [];
            setTrendData(resource.map(item => ({ month: item?.month, value: Number(item?.sales_qty) || 0 })));
        }
    }, [getSalesTrendSummaryData?.isSuccess, getSalesTrendSummaryData?.data]);

    const achieved = Math.min(sellThroughPct, 100);
    const sellThroughData = [
        { key: "achieved", value: achieved },
        { key: "remaining", value: 100 - achieved },
    ];

    return (
        <div className={`bg-white border border-borderSlateGrey rounded-[10px] p-[12px] ${getSalesTrendSummaryData?.isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
            <LanguageTranslator tag='p' className='mb-0 text-[16px] font-semibold text-fontBlack'>Sales Overview</LanguageTranslator>

            <div className='mt-[10px] flex flex-col lg:flex-row items-center gap-[12px]'>
                <div className='w-full lg:w-[220px] shrink-0 flex flex-col items-center'>
                    <LanguageTranslator tag='p' className='mb-0 capitalize text-[14px] font-semibold text-fontBlack text-center'>Sell Through %</LanguageTranslator>
                    <p className='mb-0 text-[11px] font-medium leading-[1.6] text-[#797F87] text-center'>(Total Sales Qty/Total GRC Qty)</p>

                    <div className='relative w-full h-[180px] mt-[6px]'>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={sellThroughData}
                                    dataKey="value"
                                    innerRadius="62%"
                                    outerRadius="92%"
                                    startAngle={90}
                                    endAngle={-270}
                                    stroke="none"
                                    isAnimationActive={false}
                                >
                                    <Cell fill="#51B850" />
                                    <Cell fill="#51B850" fillOpacity={0.1} />
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        <p className='mb-0 absolute inset-0 flex items-center justify-center text-[20px] font-semibold text-fontBlack pointer-events-none'>
                            {sellThroughPct}%
                        </p>
                    </div>
                </div>

                <div className='flex-1 min-w-0 w-full'>
                    <p className='mb-0 text-center text-fontBlack'>
                        <LanguageTranslator tag='span' className='capitalize text-[14px] font-semibold'>Sales Trends</LanguageTranslator>
                        <span className='text-[12px] font-normal'>(last 12 months)</span>
                    </p>

                    <div className='w-full h-[232px] mt-[4px]'>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={trendData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }} barCategoryGap="28%">
                                <XAxis
                                    dataKey="month"
                                    tickLine={false}
                                    axisLine={false}
                                    interval={0}
                                    tick={{ fill: "#4A5D7A", fontSize: 11 }}
                                />
                                <YAxis
                                    tickLine={false}
                                    axisLine={false}
                                    width={44}
                                    domain={[0, 'auto']}
                                    tickFormatter={(val) => val == 0 ? "0" : formatNumber(val).short}
                                    tick={{ fill: "#4A5D7A", fontSize: 11 }}
                                />
                                <Bar dataKey="value" fill="#5D5FEF" fillOpacity={0.8} radius={[6, 6, 2, 2]} maxBarSize={22} isAnimationActive={false} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SalesTrendsCard
